import { useCallback, useEffect, useState } from "react";
import useAuth from "../hooks/useAuth";
import { Outlet } from "react-router";
import jwt_decode, { JwtPayload } from 'jwt-decode';
import axios from "../api/axios";
import { Backdrop, Stack } from "@mui/material";
import LoadingIndicator from "../components/common/LoadingIndicator/LoadingIndicator";
import { useLocation } from "react-router-dom";
import assets from "../assets";
import { Id, toast } from "react-toastify";
import React from "react";

const PersistLogin = () => {
    const location = useLocation();
    const [time, setTime] = useState(Date.now());
    const [dateTokenExpires, setDateTokenExpires] = useState<Date>();
    const { token, user, setToken, setUser, logout } = useAuth();
    const [loading, setLoading] = useState(true);

    const verifyToken = useCallback(async () => {
        try {
            const tokenToVerify = token;
            if (tokenToVerify) {
                const response = await axios.post(`/user/refresh-token`, { token: tokenToVerify });
                setToken(response.data.token);
                const decodedToken: any = jwt_decode<JwtPayload>(response.data.token);
                const dateExpires = new Date(decodedToken.exp * 1000);
                setDateTokenExpires(dateExpires);
            }
        } catch (error) {
            logout();
        }
    }, [token, setToken, logout]);

    const verifyLocalStorageToken = useCallback(async () => {
        try {
            const tokenToVerify = localStorage.getItem('MJDP-ath');
            if (tokenToVerify) {
                setLoading(true);
                const response = await axios.post(`/user/refresh-token`, { token: tokenToVerify });
                setToken(response.data.token);
                const decodedToken: any = jwt_decode<JwtPayload>(response.data.token);
                const dateExpires = new Date(decodedToken.exp * 1000);
                setDateTokenExpires(dateExpires);
                setUser(decodedToken.data);
            }
        } catch (error) {
            logout();
        } finally {
            setLoading(false);
        }
    }, [setToken, setUser, logout]);

    // Validate the token from localStorage when there is not a token
    // We want to show a backdrop to as we validate the user's token from localStorage
    useEffect(() => { !token || !user ? verifyLocalStorageToken() : setLoading(false) }, [token, user, verifyLocalStorageToken]);

    // Validate state token everytime the user navigates and update token
    useEffect(() => { verifyToken() }, [location, verifyToken]);

    const toastId = React.useRef<Id | undefined>(undefined);

    const notifyLogout = (expiresIn: number) => {
        toastId.current = toast.warning(`Logging out due to inactivity in [${expiresIn}] seconds`,
            {
                autoClose: false,
                closeButton: false
            });
    }

    const updateNotification = (expiresIn: number) => {
        if (toastId.current) {
            toast.update(toastId.current ?? 0, {
                render: `Logging out due to inactivity in [${expiresIn}] seconds`,
            });

            if (expiresIn < 0)
                toast.done(toastId.current);
        }
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setTime(Date.now())
        }, 1000);

        return () => {
            clearInterval(interval);
        }
    }, []);

    // Handle displaying toast and logging off due to inactivity
    useEffect(() => {
        const currentDate = new Date(time);
        if (dateTokenExpires) {
            const remainingSeconds = Math.round((dateTokenExpires?.getTime() - currentDate.getTime()) / 1000);
            if (remainingSeconds < 60 && remainingSeconds > 0) {
                toastId.current
                    ? updateNotification(remainingSeconds)
                    : notifyLogout(remainingSeconds);
            } else if (remainingSeconds < 1) {
                toast.dismiss();
                logout();
            }
        }
    }, [time, dateTokenExpires, toastId, logout]);

    return (
        <>
            {loading
                ? <Backdrop open={loading} sx={{ backgroundColor: '#fff' }}>
                    <Stack direction="column" spacing={2} justifyContent="center" alignItems="center" maxWidth="350px">
                        <img width="100%" height="100%" src={assets.images.logo} alt="MadJax Logo" />
                        <LoadingIndicator size={50} loading={loading} />
                    </Stack>
                </Backdrop>
                : <Outlet />
            }
        </>
    )
}

export default PersistLogin;